import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Pencil,
  History,
  RefreshCw,
  LogOut,
  ArrowRightLeft,
  Trash2,
} from 'lucide-react';
import apiClient from '../utils/apiClient';
import { useFacility } from '../context/FacilityContext';
import PageBackLink from '../components/PageBackLink';
import { formatDate as tzFormatDate } from '../utils/datetime';

/**
 * UnitDetail — read-focused page for a single unit (ESS parity).
 *
 *   /units/:id  -> this page
 *
 * Companion routes:
 *   /units/:id/edit     -> UnitForm (the existing form)
 *   /units/:id/history  -> UnitHistory (curated + raw)
 *
 * Sections mirror the ESS Unit detail screen: a hero header with quick
 * actions, a status pill, a fields table, and a "currently rented to"
 * panel that resolves through /api/facilities/{fid}/rentals?unit_id=.
 */

const STATUS_STYLE = {
  Available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Occupied: 'bg-blue-50 text-blue-700 border-blue-200',
  Reserved: 'bg-amber-50 text-amber-700 border-amber-200',
  Maintenance: 'bg-slate-100 text-slate-700 border-slate-200',
  Pending: 'bg-violet-50 text-violet-700 border-violet-200',
  Rented: 'bg-blue-50 text-blue-700 border-blue-200',
  Late: 'bg-rose-50 text-rose-700 border-rose-200',
};

// Shared tz-aware formatter: bare dates never shift; naive-UTC instants
// render on the facility calendar (raw new Date() showed the UTC date
// every evening — the Claire Allen move-out class, 2026-08-01).
const fmtDateTz = (iso, tz) => {
  if (!iso) return '—';
  return tzFormatDate(iso, tz) || '—';
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `$${n.toFixed(2)}`;
};

const UnitDetail = () => {
  const { id: unitId } = useParams();
  const { currentFacility, facilityId } = useFacility();
  const navigate = useNavigate();
  const fmtDate = (iso) => fmtDateTz(iso, currentFacility?.timezone);

  const [unit, setUnit] = useState(null);
  const [unitType, setUnitType] = useState(null);
  const [activeRental, setActiveRental] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Delete unit -- only allowed when no active rental (UI gate matches the
  // backend's safety check; deleting a unit that ever had a rental should
  // soft-delete on the backend so the rentals fact table still resolves
  // its unit_id, but for v1 we just call the existing DELETE endpoint).
  const onDelete = async () => {
    if (!unit) return;
    if (activeRental) {
      alert('Cannot delete a unit with an active rental. Move out the tenant first.');
      return;
    }
    if (!window.confirm(
      `Permanently delete Unit ${unit.unit_number}?\n\n` +
      `This removes the unit from your inventory. Any historical rentals/payments tied to this unit_id will still resolve, but the unit will not be available for future rentals.`
    )) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/api/facilities/${facilityId}/units/${unitId}`);
      navigate('/units');
    } catch (e) {
      alert(e?.response?.data?.detail || e.message || 'Delete failed');
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    if (!facilityId || !unitId) return;
    setLoading(true);
    setError(null);
    try {
      // Unit + active rental in parallel. Rentals list is facility-scoped;
      // we filter to this unit client-side (the list payload is small).
      const [unitRes, rentalsRes] = await Promise.all([
        apiClient.get(`/api/facilities/${facilityId}/units/${unitId}`),
        apiClient.get(`/api/facilities/${facilityId}/rentals/?include_archived=false`),
      ]);
      const u = unitRes.data;
      setUnit(u);

      // Resolve the parent unit_type for size / deposit / etc.
      if (u?.unit_type_id) {
        try {
          const utRes = await apiClient.get(
            `/api/facilities/${facilityId}/unit-types/${u.unit_type_id}`,
          );
          setUnitType(utRes.data);
        } catch {
          setUnitType(null);
        }
      } else {
        setUnitType(null);
      }

      // Find the active rental on this unit (if any). Active = not ended,
      // not draft. We accept Active/Late/Delinquent here.
      const rentals = rentalsRes.data || [];
      const active = rentals.find(
        (r) =>
          r.unit_id === unitId &&
          r.status &&
          r.status !== 'Ended' &&
          r.status !== 'draft',
      );
      setActiveRental(active || null);

      if (active?.customer_id) {
        try {
          const cRes = await apiClient.get(
            `/api/facilities/${facilityId}/customers/${active.customer_id}`,
          );
          setCustomer(cRes.data);
        } catch {
          setCustomer(null);
        }
      } else {
        setCustomer(null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [facilityId, unitId]);

  useEffect(() => { load(); }, [load]);

  if (!facilityId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
      </div>
    );
  }

  const sizeLabel = (() => {
    if (unitType?.name) {
      const dims = unit?.width && unit?.length ? ` (${unit.width} x ${unit.length})` : '';
      return `${unitType.name}${dims}`;
    }
    if (unit?.unit_type) {
      const dims = unit?.width && unit?.length ? ` (${unit.width} x ${unit.length})` : '';
      return `${unit.unit_type}${dims}`;
    }
    return '—';
  })();

  const monthlyRate = unit?.monthly_rate;
  const billingCycleLabel = activeRental?.monthly_rate
    ? `${fmtMoney(activeRental.monthly_rate)} Each Month`
    : monthlyRate
      ? `${fmtMoney(monthlyRate)} Each Month`
      : '—';
  const pricingOptionsLabel = monthlyRate ? `${fmtMoney(monthlyRate)} / 1 month` : '—';

  const status = unit?.status || 'Available';
  const statusClass = STATUS_STYLE[status] || 'bg-slate-100 text-slate-700 border-slate-200';

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <PageBackLink to="/units" label="Back to units" />
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-3">
            Unit {unit?.unit_number || '…'}
            {unit && (
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${statusClass}`}>
                {status}
              </span>
            )}
          </h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            {currentFacility?.name || ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 hover:border-slate-300 rounded text-[12px] text-slate-700"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <Link
            to={`/units/${unitId}/history`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 hover:border-slate-300 rounded text-[12px] text-slate-700"
          >
            <History className="h-3.5 w-3.5" /> Unit History
          </Link>
          <button
            onClick={() => navigate(`/units/${unitId}/edit`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 hover:border-slate-300 rounded text-[12px] text-slate-700"
            title="Change Status"
          >
            <ArrowRightLeft className="h-3.5 w-3.5" /> Change Status
          </button>
          {activeRental && (
            <button
              onClick={() => navigate(`/rentals/${activeRental._id || activeRental.id}/edit`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rose-200 hover:border-rose-300 text-rose-700 rounded text-[12px]"
              title="Move Out"
            >
              <LogOut className="h-3.5 w-3.5" /> Move Out
            </button>
          )}
          <Link
            to={`/units/${unitId}/edit`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-medium"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Link>
          {!activeRental && (
            <button
              onClick={onDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rose-200 hover:border-rose-300 hover:bg-rose-50 text-rose-700 rounded text-[12px] disabled:opacity-50"
              title="Delete unit"
            >
              <Trash2 className="h-3.5 w-3.5" /> {deleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200/80 bg-rose-50/50 p-3 mb-4">
          <p className="text-[12px] text-rose-700">{error}</p>
        </div>
      )}

      {loading && !unit ? (
        <div className="rounded-xl border border-slate-200/80 bg-white p-6 text-[13px] text-slate-400">
          Loading…
        </div>
      ) : !unit ? (
        <div className="rounded-xl border border-slate-200/80 bg-white p-6 text-[13px] text-slate-400">
          Unit not found.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Static fields table — main column */}
          <div className="lg:col-span-2 rounded-xl border border-slate-200/80 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <tbody>
                  <Row label="Name or Number" value={unit.unit_number} />
                  <Row label="Size" value={sizeLabel} />
                  <Row label="Pricing Options" value={pricingOptionsLabel} />
                  <Row label="Deposit" value={fmtMoney(unitType?.deposit ?? unitType?.deposit_amount)} />
                  <Row label="Admin Fee" value={fmtMoney(unitType?.admin_fee)} />
                  <Row label="Reservation Price" value={fmtMoney(unitType?.reservation_price)} />
                  <Row
                    label="Status"
                    value={
                      <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium border ${statusClass}`}>
                        {status}
                      </span>
                    }
                  />
                  <Row label="Customer Access" value="Not locked out" />
                  <Row
                    label="Rented To"
                    value={
                      activeRental && customer ? (
                        <Link
                          to={`/customers/${customer._id || customer.id}/edit`}
                          className="text-blue-600 hover:underline"
                        >
                          {customer.full_name || customer.name || '—'}
                        </Link>
                      ) : activeRental ? (
                        <span className="text-slate-500">Rental {activeRental._id}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )
                    }
                  />
                  {activeRental && (
                    <>
                      <Row label="Rental Start" value={fmtDate(activeRental.start_date)} />
                      <Row label="Billing Cycle" value={billingCycleLabel} />
                      <Row label="Next Bill" value={fmtDate(activeRental.next_bill_date || activeRental.next_billing_date)} />
                    </>
                  )}
                  <Row
                    label="Notes"
                    value={unit.notes ? <span className="whitespace-pre-wrap">{unit.notes}</span> : <span className="text-slate-400">—</span>}
                  />
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
              <span>Created: {fmtDate(unit.created_at)}</span>
              <span>Updated: {fmtDate(unit.updated_at)}</span>
              {unit.created_by && <span>By: {unit.created_by}</span>}
            </div>
          </div>

          {/* Side panel — quick facts */}
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200/80 bg-white p-4">
              <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Unit Type</h3>
              {unitType ? (
                <>
                  <p className="text-[14px] font-medium text-slate-900">{unitType.name}</p>
                  {unitType.description && (
                    <p className="text-[12px] text-slate-500 mt-1">{unitType.description}</p>
                  )}
                  <p className="text-[12px] text-slate-500 mt-2">
                    Type rate: {fmtMoney(unitType.monthly_rate)}/mo
                  </p>
                  {unit.rate_override !== null && unit.rate_override !== undefined && (
                    <p className="text-[12px] text-amber-700 mt-1">
                      Per-unit override active: {fmtMoney(unit.rate_override)}/mo
                    </p>
                  )}
                </>
              ) : (
                <p className="text-[12px] text-slate-400">{unit.unit_type || 'No type assigned'}</p>
              )}
            </div>

            {activeRental && (
              <div className="rounded-xl border border-blue-200/60 bg-blue-50/40 p-4">
                <h3 className="text-[12px] font-semibold text-blue-700 uppercase tracking-wide mb-2">Currently Rented</h3>
                {/* The tenant's NAME is the link. This card is where the eye
                    lands, but it used to render the name as flat text and
                    offer only "Open rental" — so from a unit there was no
                    obvious route to the person renting it. The name in the
                    detail table was a link, but nothing about it said so. */}
                {customer ? (
                  <Link
                    to={`/customers/${customer._id || customer.id}/edit`}
                    className="text-[14px] font-medium text-blue-700 hover:underline"
                  >
                    {customer.full_name || customer.name || 'Tenant'}
                  </Link>
                ) : (
                  <p className="text-[14px] font-medium text-slate-900">Tenant</p>
                )}
                <p className="text-[12px] text-slate-600 mt-0.5">
                  Started {fmtDate(activeRental.start_date)}
                </p>
                <p className="text-[12px] text-slate-600">
                  Balance: {fmtMoney(activeRental.balance ?? 0)}
                </p>
                <div className="mt-2 flex gap-2">
                  <Link
                    to={`/rentals/${activeRental._id || activeRental.id}/edit`}
                    className="text-[12px] text-blue-700 hover:underline"
                  >
                    Open rental →
                  </Link>
                  {customer && (
                    <Link
                      to={`/customers/${customer._id || customer.id}/edit`}
                      className="text-[12px] text-blue-700 hover:underline"
                    >
                      Open customer →
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <tr className="border-b border-slate-100 last:border-0">
    <td className="px-4 py-2.5 text-slate-500 w-48 align-top">{label}</td>
    <td className="px-4 py-2.5 text-slate-800">{value ?? '—'}</td>
  </tr>
);

export default UnitDetail;
